import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import axios from 'axios';

// Shared pieces for the secrets rotation tracker pages.

export const KEY_STATUS_LABEL = {
  open: 'Open',
  eligible: 'Eligible to revoke',
  revoked: 'Revoked',
  false_positive: 'False positive',
  wont_fix: "Won't fix",
};

export const REPO_STATUS_LABEL = { open: 'Open', ready_to_revoke: 'Ready to revoke' };

export const FP_REASON_LABEL = {
  placeholder: 'Placeholder or example value',
  identifier: 'Identifier, not a credential',
  test_fixture: 'Test or fixture data',
  public: 'Public value',
  encrypted: 'Encrypted or hashed',
  pattern: 'Scanner misfires on this shape',
  other: 'Other',
};

// Reason fields shown whenever a status is being set to False positive.
export function ReasonFields({ reason, detail, onChange, compact = false }) {
  return (
    <>
      <div style={{ width: compact ? 220 : undefined }}>
        <label className="form-label">Why is it not a secret? *</label>
        <select className="form-input" value={reason || ''} onChange={(e) => onChange({ reason: e.target.value, detail })}>
          <option value="">Pick a reason</option>
          {Object.entries(FP_REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div style={{ width: compact ? 260 : undefined }}>
        <label className="form-label">Detail (helps the learner)</label>
        <input className="form-input" value={detail || ''} placeholder="e.g. Razorpay key id, not the secret"
          onChange={(e) => onChange({ reason, detail: e.target.value })} />
      </div>
    </>
  );
}

export function FpMark({ r }) {
  if (r.status !== 'false_positive') return null;
  const auto = r.fp_marked_by === 'auto';
  return (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
      {FP_REASON_LABEL[r.fp_reason] || r.fp_reason || 'no reason'}
      {auto && (
        <span style={{ color: r.fp_reviewed_at ? 'var(--text-muted)' : '#fbbf24' }}>
          {' · auto'}{r.fp_rule_id ? ` rule #${r.fp_rule_id}` : ''}{r.fp_reviewed_at ? '' : ' · unreviewed'}
        </span>
      )}
    </div>
  );
}

const KEY_STATUS_STYLE = {
  open:           { bg: 'rgba(100,116,139,0.2)', fg: '#cbd5e1' },
  eligible:       { bg: 'rgba(99,102,241,0.22)', fg: '#a5b4fc' },
  revoked:        { bg: 'rgba(34,197,94,0.2)',   fg: '#4ade80' },
  false_positive: { bg: 'rgba(100,116,139,0.12)', fg: '#94a3b8' },
  wont_fix:       { bg: 'rgba(100,116,139,0.12)', fg: '#94a3b8' },
};

export function errMsg(err) {
  return err?.response?.data?.error || err?.message || 'Request failed';
}

export function KeyStatusBadge({ status }) {
  const s = KEY_STATUS_STYLE[status] || KEY_STATUS_STYLE.open;
  return (
    <span className="badge" style={{ whiteSpace: 'nowrap', background: s.bg, color: s.fg }}>
      {status === 'revoked' ? '✓ ' : status === 'eligible' ? '● ' : ''}{KEY_STATUS_LABEL[status] || status}
    </span>
  );
}

export function RepoStatusBadge({ status }) {
  const ready = status === 'ready_to_revoke';
  return (
    <span className="badge" style={{
      background: ready ? 'rgba(99,102,241,0.22)' : 'rgba(100,116,139,0.2)',
      color: ready ? '#a5b4fc' : '#cbd5e1',
    }}>{ready ? '✓ ' : ''}{REPO_STATUS_LABEL[status] || status}</span>
  );
}

export function RiskBadge({ risk, type }) {
  if (!risk) {
    if (type && type !== 'AWS') return <span className="badge badge-low" style={{ whiteSpace: 'nowrap' }} title="Found after the rated scan">Unrated</span>;
    return <span className="badge badge-low" style={{ whiteSpace: 'nowrap' }} title="AWS key handled by the AWS runbook">AWS runbook</span>;
  }
  return <span className={`badge badge-${risk.toLowerCase()}`} style={{ whiteSpace: 'nowrap' }}>{risk}</span>;
}

// A key the presence scan found after the 23 Sep baseline.
export function NewBadge({ since }) {
  return (
    <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(239,68,68,0.16)', color: '#fca5a5' }}
      title={`Not in the 23 Sep scan; first found ${since ? fmtDate(since) : 'by a later presence scan'}`}>
      New since 23 Sep
    </span>
  );
}

export function PresenceBadge({ present, removedAt, reason }) {
  if (!present && reason === 'repo_archived') {
    return (
      <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(34,197,94,0.14)', color: '#86efac' }}
        title={`Repo archived on Bitbucket${removedAt ? ` (detected ${fmtDate(removedAt)})` : ''}. It is no longer scanned, so the key counts as gone from code; it is still in that repo's git history.`}>
        Repo archived
      </span>
    );
  }
  if (present) {
    return <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(245,158,11,0.18)', color: '#fbbf24' }}
      title="Still in the default branch's current code">In code</span>;
  }
  return (
    <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(34,197,94,0.14)', color: '#86efac' }}
      title={removedAt ? `Removed from current code, detected ${fmtDate(removedAt)}` : 'Only in git history'}>
      {removedAt ? 'Removed' : 'History only'}
    </span>
  );
}

export const LIVENESS_LABEL = {
  unknown: 'Not analysed', analyzing: 'Analysing\u2026', awaiting_probe: 'Probe ready',
  probing: 'Probing\u2026', active: 'Active', maybe_inactive: 'May be inactive',
  unreachable: 'Unreachable', unverified: 'Unverified', error: 'Analysis failed',
};

const LIVENESS_STYLE = {
  active:         { bg: 'rgba(239,68,68,0.18)',  fg: '#fca5a5', dot: '\u25cf' },
  awaiting_probe: { bg: 'rgba(245,158,11,0.18)', fg: '#fbbf24', dot: '\u25cb' },
  probing:        { bg: 'rgba(99,102,241,0.2)',  fg: '#a5b4fc', dot: '\u25cc' },
  analyzing:      { bg: 'rgba(99,102,241,0.2)',  fg: '#a5b4fc', dot: '\u25cc' },
  maybe_inactive: { bg: 'rgba(34,197,94,0.14)',  fg: '#86efac', dot: '\u25cb' },
  unreachable:    { bg: 'rgba(100,116,139,0.18)', fg: '#cbd5e1', dot: '\u25cb' },
  unverified:     { bg: 'rgba(100,116,139,0.14)', fg: '#94a3b8', dot: '\u25cb' },
  error:          { bg: 'rgba(239,68,68,0.12)',  fg: '#f87171', dot: '!' },
  unknown:        { bg: 'rgba(100,116,139,0.12)', fg: '#94a3b8', dot: '\u2022' },
};

// Deep-analysis liveness of a key. Clickable so a reader can open the result.
export function LivenessBadge({ liveness, onClick, title }) {
  const l = liveness || 'unknown';
  const st = LIVENESS_STYLE[l] || LIVENESS_STYLE.unknown;
  const label = LIVENESS_LABEL[l] || l;
  const spin = l === 'analyzing' || l === 'probing';
  const common = { whiteSpace: 'nowrap', background: st.bg, color: st.fg,
    cursor: onClick ? 'pointer' : 'default', border: 'none' };
  const inner = <>{spin ? '' : `${st.dot} `}{label}{spin && <span className="spinner" style={{ marginLeft: 6, width: 10, height: 10 }} />}</>;
  if (onClick) return <button type="button" className="badge" style={common} onClick={onClick} title={title || 'View deep analysis'}>{inner}</button>;
  return <span className="badge" style={common} title={title}>{inner}</span>;
}

export function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function pct(n, d) {
  if (!d) return 0;
  return Math.round((100 * n) / d);
}

// Single-proportion bar. The number is always shown as text beside it.
export function ProgressBar({ value, total, color = 'var(--accent)', title }) {
  const p = total ? Math.min(100, (100 * value) / total) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }} title={title}>
      <div style={{ flex: 1, height: 8, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 36, textAlign: 'right' }}>
        {Math.round(p)}%
      </span>
    </div>
  );
}

// Cleanup progress across the files that held a key in current code. A key
// only counts as gone from a repo once every one of them is clean, so this is
// the only sign of a partial fix. Hidden when nothing is tracked or all is clean.
export function FileProgress({ total, left, style }) {
  if (!total || !left) return null;
  const cleaned = total - left;
  return (
    <div style={{ fontSize: 11, marginTop: 4, ...style }}
      title={`${cleaned} of ${total} files that held this key in current code no longer do. It counts as gone only when all ${total} are clean.`}>
      <div style={{ color: cleaned ? '#86efac' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
        {cleaned} of {total} file{total === 1 ? '' : 's'} cleaned
      </div>
      <div style={{ height: 4, width: 110, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
        <div style={{ width: `${(100 * cleaned) / total}%`, height: '100%', background: 'var(--success)' }} />
      </div>
    </div>
  );
}

// One place a key can be seen on Bitbucket, shown on every key list.
export function SampleLink({ r }) {
  if (!r.sample_link) return null;
  const file = r.sample_file || '';
  const short = file.length > 48 ? `…${file.slice(-46)}` : file;
  return (
    <div style={{ fontSize: 11, marginTop: 3, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <a href={r.sample_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
        title={`${r.sample_repo}/${file}${r.sample_line ? `:${r.sample_line}` : ''}${r.sample_commit ? ` at commit ${r.sample_commit.slice(0, 10)}` : ''}`}>
        {r.sample_repo}/{short}{r.sample_line ? `:${r.sample_line}` : ''} ↗
      </a>
    </div>
  );
}

export function JiraLink({ jiraKey, base }) {
  if (!jiraKey) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (!base) return <span>{jiraKey}</span>;
  return <a href={`${base}/browse/${jiraKey}`} target="_blank" rel="noreferrer">{jiraKey}</a>;
}

export function Pager({ page, limit, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (total <= limit) return <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 10 }}>{total} total</div>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, fontSize: 13 }}>
      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span style={{ color: 'var(--text-secondary)' }}>Page {page} of {pages} · {total} total</span>
      <button className="btn btn-secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

// ── Access ────────────────────────────────────────────────────────────────────
// The API decides who can see the tracker (owner + share list); the UI asks it
// once per login token and hides what the caller cannot use.
let accessCache = { token: null, promise: null };
export function loadSecretsAccess() {
  const token = localStorage.getItem('pf_token');
  if (!token) return Promise.resolve(null);
  if (accessCache.token !== token || !accessCache.promise) {
    accessCache = {
      token,
      promise: axios.get('/secrets/me').then((r) => r.data).catch(() => null),
    };
  }
  return accessCache.promise;
}
export function resetSecretsAccess() { accessCache = { token: null, promise: null }; }

export function useSecretsAccessLoader() {
  const [state, setState] = useState({ loading: true, access: null });
  useEffect(() => {
    let live = true;
    loadSecretsAccess().then((a) => { if (live) setState({ loading: false, access: a }); });
    return () => { live = false; };
  }, []);
  return state;
}

export const SecretsAccessContext = createContext({ role: 'viewer', can_edit: false, is_owner: false });
export function useSecretsAccess() { return useContext(SecretsAccessContext); }

export function SecretsTabs() {
  const access = useSecretsAccess();
  const tab = ({ isActive }) => ({
    padding: '8px 14px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600,
    color: isActive ? '#fff' : 'var(--text-secondary)',
    background: isActive ? 'var(--accent)' : 'transparent', textDecoration: 'none',
  });
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
      <NavLink end to="/secrets" style={tab}>Progress</NavLink>
      <NavLink to="/secrets/keys" style={tab}>Keys</NavLink>
      <NavLink to="/secrets/repos" style={tab}>Repos</NavLink>
      <NavLink to="/secrets/gone" style={tab}>Gone from code</NavLink>
      <NavLink to="/secrets/rules" style={tab}>Rules</NavLink>
      <NavLink to="/secrets/activity" style={tab}>Activity</NavLink>
      {access.is_owner && <NavLink to="/secrets/sharing" style={tab}>Sharing</NavLink>}
      {!access.can_edit && (
        <span style={{ alignSelf: 'center', marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>Read-only access</span>
      )}
    </div>
  );
}

// Repo archived on Bitbucket: not scanned, its keys count as gone from code.
export function ArchivedBadge({ at, project }) {
  if (!at) return null;
  return (
    <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(100,116,139,0.2)', color: '#cbd5e1' }}
      title={`In Bitbucket project ${project || 'Archive'} since ${fmtDate(at)} or earlier. Not scanned any more; keys still in its code count as gone.`}>
      Archived
    </span>
  );
}

// Bitbucket answers 404 for this repo: deleted, renamed, or hidden from the app's account.
export function MissingBadge({ at }) {
  if (!at) return null;
  return (
    <span className="badge" style={{ whiteSpace: 'nowrap', background: 'rgba(239,68,68,0.15)', color: '#fca5a5' }}
      title={`Bitbucket says this repo does not exist (first seen ${fmtDate(at)}): deleted, renamed, or not visible to the app's account. It cannot be scanned; its keys still count as they did at the last scan.`}>
      Not on Bitbucket
    </span>
  );
}

export function PageHeader({ title, children }) {
  return (
    <div className="page-header">
      <h2>{title}</h2>
      <p>
        {children || (
          <>Bitbucket secrets rotation programme. Keys are stored only as SHA-256 digests.
            Risk ratings, key uses and repo owners came from the 2026-09-23 scan and are unreviewed guesses.</>
        )}
      </p>
    </div>
  );
}

// Filters live in the URL so views can be bookmarked and the back button works.
export function useUrlFilters(defaults = {}) {
  const [params, setParams] = useSearchParams();
  const get = (k) => params.get(k) ?? defaults[k] ?? '';
  const set = (patch, { keepPage = false } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined || (Array.isArray(v) && !v.length)) next.delete(k);
      else next.set(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    if (!keepPage && !('page' in patch)) next.delete('page');
    setParams(next, { replace: false });
  };
  return { params, get, set };
}

// Table header that sorts the list by `sortKey` through the URL (sort, dir).
// Clicking the current column flips the direction; a new column starts at
// `firstDir` (asc for names, desc for counts).
export function SortTh({ sortKey, label, get, set, defaultSort, defaultDir = 'desc', firstDir = 'desc', style }) {
  const cur = get('sort') || defaultSort;
  const dir = get('dir') || defaultDir;
  const on = cur === sortKey;
  const click = () => set(on ? { sort: sortKey, dir: dir === 'asc' ? 'desc' : 'asc' } : { sort: sortKey, dir: firstDir });
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }} onClick={click}
      aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} title={`Sort by ${label}`}>
      {label}{' '}
      <span style={{ color: on ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 11 }}>
        {on ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}

let filtersCache = null;
export function useSecretFilters() {
  const [f, setF] = useState(filtersCache);
  useEffect(() => {
    if (filtersCache) return;
    axios.get('/secrets/filters').then((r) => { filtersCache = r.data; setF(r.data); }).catch(() => {});
  }, []);
  return f;
}
export function invalidateFilters() { filtersCache = null; }

// Toggle chips for multi-value filters (status, risk).
export function ChipGroup({ options, value, onChange, labels = {} }) {
  const sel = new Set((value || '').split(',').filter(Boolean));
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = sel.has(o);
        return (
          <button key={o} type="button" className="btn"
            style={{
              padding: '4px 10px', fontSize: 12,
              background: on ? 'var(--accent-light)' : 'var(--bg-elevated)',
              color: on ? '#c7d2fe' : 'var(--text-secondary)',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
            }}
            onClick={() => {
              const next = new Set(sel);
              if (on) next.delete(o); else next.add(o);
              onChange([...next].join(','));
            }}>{labels[o] || o}</button>
        );
      })}
    </div>
  );
}

// Employee picker backed by the existing /employees proxy.
export function PersonPicker({ email, name, onChange, placeholder = 'Search the employee directory' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      axios.get('/employees', { params: { q } })
        .then((r) => { setResults(r.data.data || []); setOpen(true); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);
  return (
    <div style={{ position: 'relative' }}>
      {email ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{name || email} <small style={{ color: 'var(--text-muted)' }}>{name ? email : ''}</small></span>
          <button type="button" className="btn btn-ghost" onClick={() => onChange({ email: '', name: '' })}>Clear</button>
        </div>
      ) : (
        <input className="form-input" value={q} placeholder={placeholder}
          onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
      )}
      {open && !email && results.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          maxHeight: 240, overflowY: 'auto', boxShadow: 'var(--shadow-lg)',
        }}>
          {results.slice(0, 20).map((e) => (
            <div key={e.email || e.id} style={{ padding: '8px 12px', cursor: 'pointer' }}
              onMouseDown={() => { onChange({ email: e.email, name: e.name }); setQ(''); setOpen(false); }}>
              {e.name} <small style={{ color: 'var(--text-muted)' }}>{e.email}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Raw-key search. The value is sent in a POST body, never in the URL, and the
// field is cleared as soon as the request is sent.
export function RawKeySearch({ onResults }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const v = value;
    if (!v.trim()) return;
    setValue('');
    setBusy(true);
    setMsg('');
    axios.post('/secrets/keys/search', { value: v })
      .then((r) => {
        const rows = r.data.data || [];
        setMsg(rows.length ? `${rows.length} match${rows.length > 1 ? 'es' : ''}` : 'No tracked key has that value');
        onResults(rows);
      })
      .catch((err) => setMsg(errMsg(err)))
      .finally(() => setBusy(false));
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <textarea className="form-input" rows={1} value={value} autoComplete="off" spellCheck={false}
        placeholder="Paste a raw key to find it"
        title="Hashed before lookup. The value is never stored or put in the URL."
        style={{ width: 420, maxWidth: '100%', minHeight: 36, height: 36, fontFamily: 'monospace', WebkitTextSecurity: 'disc' }}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submit(e); }} />
      <button className="btn btn-primary" disabled={busy || !value.trim()}>Find key</button>
      {busy && <span className="spinner" />}
      {msg ? <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{msg}</span>
        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Hashed with SHA-256 before lookup, never stored.</span>}
    </form>
  );
}

export function Field({ label, children }) {
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export function Kv({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ marginTop: 2 }}>{children}</div>
    </div>
  );
}
